import React from 'react';
import ReactECharts from 'echarts-for-react';
import { useCameraData } from '../hooks/useCameraData';

const EmotionRadialChart = ({ artworkId, embedded = false }) => {
  const { rawData, loading, error } = useCameraData(artworkId, 'emotion');
  const shellClass = embedded
    ? 'w-full h-full flex items-center justify-center text-center text-white'
    : 'w-full max-w-3xl bg-gray-950 rounded-xl p-4 shadow-2xl shadow-black/50 backdrop-blur-sm border border-gray-800 flex items-center justify-center h-full text-center text-white';

  if (loading) {
    return <div className={shellClass}>Loading emotion data...</div>;
  }

  if (error) {
    return <div className={`${shellClass} text-red-400`}>{error}</div>;
  }

  if (!rawData || rawData.length === 0) {
    return <div className={`${shellClass} text-gray-300`}>No emotion data available for this artwork</div>;
  }

  // Process data for radar chart format
  const emotions = ["positive", "neutral", "negative"];
  const maxDuration = Math.max(...rawData.map(d => d.Session_Duration || d.session_duration || 0));
  
  const processedData = emotions.map(emotion => {
    const emotionData = rawData.filter(d => {
      const sessionEmotion = (d.Emotion || d.emotion || '').toLowerCase();
      return sessionEmotion === emotion;
    });
    const avgDuration = emotionData.length > 0 
      ? emotionData.reduce((acc, curr) => {
          const duration = curr.Session_Duration || curr.session_duration || 0;
          return acc + duration;
        }, 0) / emotionData.length
      : 0;
    
    return {
      emotion: emotion.charAt(0).toUpperCase() + emotion.slice(1),
      value: maxDuration > 0 ? avgDuration / maxDuration : 0,
      avgDuration: avgDuration.toFixed(2)
    };
  });

  // Emotion colors for tooltip
  const emotionColors = {
    Positive: '#10b981',   // Emerald Green
    Neutral: '#f59e0b',    // Amber
    Negative: '#ef4444'    // Red
  };

  // Create gradient for radar area (blended gradient)
  const radarGradient = {
    type: 'radial',
    x: 0.5,
    y: 0.5,
    r: 0.7,
    colorStops: [
      { offset: 0, color: 'rgba(16, 185, 129, 0.5)' },   // Emerald Green
      { offset: 0.33, color: 'rgba(245, 158, 11, 0.4)' }, // Amber
      { offset: 0.66, color: 'rgba(239, 68, 68, 0.4)' },  // Red
      { offset: 1, color: 'rgba(16, 185, 129, 0.3)' }     // Emerald Green (blend)
    ]
  };

  const option = {
    title: {
      text: 'Average Emotion Duration',
      left: 'center',
      bottom: '3%',
      textStyle: {
        fontSize: 16,
        fontWeight: 'bold',
        color: '#ffffff'
      }
    },
    tooltip: {
      trigger: 'item',
      appendToBody: embedded,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      borderColor: 'rgba(255, 255, 255, 0.3)',
      borderWidth: 1,
      textStyle: {
        color: '#fff',
        fontSize: 14
      },
      padding: [10, 15],
      formatter: function(params) {
        const data = processedData[params.dataIndex];
        const emotionColor = emotionColors[data.emotion] || '#999';
        return `<div style="font-weight: bold; margin-bottom: 5px;">${data.emotion}</div>Avg Duration: <span style="color: ${emotionColor}; font-weight: bold;">${data.avgDuration}s</span><br/>Value: <span style="color: ${emotionColor}; font-weight: bold;">${(data.value * 100).toFixed(1)}%</span>`;
      }
    },
    radar: {
      indicator: processedData.map((item, index) => ({
        name: item.emotion,
        max: 1,
        nameGap: 8,
        splitNumber: 4
      })),
      center: ['50%', '50%'],
      radius: '65%',
      startAngle: 90,
      axisName: {
        fontSize: 15,
        fontWeight: 'bold',
        color: '#e0e0e0',
        padding: [5, 0]
      },
      splitArea: {
        areaStyle: {
          color: [
            'rgba(16, 185, 129, 0.15)',
            'rgba(245, 158, 11, 0.12)',
            'rgba(239, 68, 68, 0.10)',
            'rgba(16, 185, 129, 0.08)'
          ]
        }
      },
      axisLine: {
        lineStyle: {
          color: 'rgba(255, 255, 255, 0.3)',
          width: 2
        }
      },
      splitLine: {
        lineStyle: {
          color: 'rgba(255, 255, 255, 0.2)',
          width: 1.5
        }
      }
    },
    series: [
      {
        name: 'Average Duration',
        type: 'radar',
        data: [
          {
            value: processedData.map(item => item.value),
            name: 'Emotions',
            areaStyle: {
              color: radarGradient
            },
            lineStyle: {
              color: '#10b981',
              width: 4,
              shadowBlur: 20,
              shadowColor: 'rgba(16, 185, 129, 0.9)',
              shadowOffsetX: 0,
              shadowOffsetY: 0
            },
            itemStyle: {
              color: '#10b981',
              shadowBlur: 20,
              shadowColor: 'rgba(16, 185, 129, 0.8)',
              shadowOffsetX: 0,
              shadowOffsetY: 0,
              borderWidth: 3,
              borderColor: '#fff'
            },
            emphasis: {
              lineStyle: {
                shadowBlur: 35,
                shadowColor: 'rgba(16, 185, 129, 1)',
                width: 5
              },
              itemStyle: {
                shadowBlur: 35,
                shadowColor: 'rgba(16, 185, 129, 1)',
                borderWidth: 4
              },
              areaStyle: {
                color: {
                  type: 'radial',
                  x: 0.5,
                  y: 0.5,
                  r: 0.7,
                  colorStops: [
                    { offset: 0, color: 'rgba(16, 185, 129, 0.7)' },
                    { offset: 0.33, color: 'rgba(245, 158, 11, 0.6)' },
                    { offset: 0.66, color: 'rgba(239, 68, 68, 0.6)' },
                    { offset: 1, color: 'rgba(16, 185, 129, 0.5)' }
                  ]
                }
              }
            }
          }
        ],
        animationDuration: 1200,
        animationEasing: 'elasticOut',
        animationDelay: function (idx) {
          return idx * 200;
        }
      }
    ]
  };

  return (
    <div className={embedded ? 'w-full h-full' : 'w-full max-w-3xl bg-gray-950 rounded-xl p-4 shadow-2xl shadow-black/50 backdrop-blur-sm border border-gray-800'}>
      <div className={embedded ? 'h-full' : 'h-80'}>
        <ReactECharts option={option} style={{ height: '100%', width: '100%' }} />
      </div>
    </div>
  );
};

export default EmotionRadialChart;